/**
 * The ONE sanitiser for signature HTML that goes into a composer as a
 * signature block (#970). Unlike `toEditorSafeHtml`, it KEEPS the builder's
 * email-safe layout — tables and inline styles — because the block renders
 * the markup as-is instead of converting it to editor nodes.
 *
 * It runs on every way markup can reach a block: insert (`buildSignatureHtml`),
 * parse (`SignatureBlockExtension.parseHTML`, so stored or pasted HTML is
 * re-checked) and paste (`toEditorSafeHtml`'s block pass-through).
 *
 * Its own DOMPurify instance, so the hooks below never leak into another
 * caller of the global one.
 */
import createDOMPurify from 'dompurify';

/** The wrapper attribute that marks a signature block; its value is the signature id. */
export const SIGNATURE_BLOCK_ATTR = 'data-ncrm-signature';

const ALLOWED_TAGS = [
    'table', 'tbody', 'thead', 'tr', 'td', 'th',
    'div', 'span', 'p', 'a', 'img', 'br', 'hr',
    'strong', 'em', 'b', 'i', 'u',
];

const ALLOWED_ATTR = [
    'style', 'href', 'src', 'alt', 'title', 'width', 'height',
    'cellpadding', 'cellspacing', 'border', 'role', 'colspan', 'rowspan',
    'align', 'valign', 'bgcolor', 'target', 'rel',
];

// Links and images: web, mail and phone links only. `data:` (allowed on <img>
// by DOMPurify's defaults) is refused by the hook below.
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|tel:)/i;

// CSS that can execute or fetch: drop the whole style attribute. A backslash
// is refused outright, so CSS escapes (`\75 rl(`) cannot hide `url(`.
const UNSAFE_STYLE = /expression\s*\(|javascript:|vbscript:|url\s*\(|image-set\s*\(|@import|behavior\s*:|-moz-binding|\\/i;

// CSS that can lift the signature out of its box — e.g. an invisible link laid
// over the whole app. Signatures are tables; they never need positioning.
const POSITIONING_STYLE = /(^|;)\s*(position|z-index|inset|top|left|right|bottom|transform|translate)\s*:/i;

let purifier = null;

function getPurifier() {
    if (purifier) return purifier;
    if (typeof window === 'undefined') return null;
    // The module is a factory; some test mocks hand back a ready instance.
    purifier = typeof createDOMPurify === 'function' ? createDOMPurify(window) : createDOMPurify;
    if (typeof purifier?.addHook !== 'function') return purifier;
    purifier.addHook('uponSanitizeAttribute', (_node, data) => {
        const name = data.attrName;
        const value = String(data.attrValue || '');
        if ((name === 'src' || name === 'href') && /^\s*data:/i.test(value)) {
            data.keepAttr = false;
        }
        if (name === 'style' && (UNSAFE_STYLE.test(value) || POSITIONING_STYLE.test(value))) {
            data.keepAttr = false;
        }
    });
    // A link that opens a new window must not hand the opener to it.
    purifier.addHook('afterSanitizeAttributes', (node) => {
        if (node.tagName === 'A' && node.hasAttribute('target')) {
            node.setAttribute('rel', 'noopener noreferrer');
        }
    });
    return purifier;
}

/** Sanitised signature HTML, or '' when there is nothing left (or no DOM). */
export function sanitizeSignatureHtml(html) {
    if (!html || typeof html !== 'string') return '';
    const p = getPurifier();
    if (!p) return '';
    return p.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOWED_URI_REGEXP,
        KEEP_CONTENT: true,
    })
        // TipTap drops newline-only whitespace between tags when it parses
        // content. Dropping it here too makes the block a fixed point from the
        // first insert, not only after one round-trip.
        .replace(/>\s*\n\s*</g, '><')
        .trim();
}
